'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { Ql, $l, MarketPrompt, MarketImage } from './market-primitives';
import { wu, K, Tu, Du, Ou, Eu, Au, ku } from './launch-plan';
import { U, MarketReveal, Xl } from './collection-animation';
import { ge } from './reference-products';
import { ShowcaseIcon } from './scene-primitives';
import { referenceImages } from './reference-brands';
export function useTypedCopy() {
  let e = React.useRef(null),
    t = React.useRef(null),
    [n, r] = React.useState(0);
  return (
    React.useLayoutEffect(() => {
      let n = e.current,
        i = t.current;
      if (!n || !i) return;
      let a = () => r(Math.max(0, i.offsetHeight - n.clientHeight));
      a();
      let o = new ResizeObserver(a);
      return (o.observe(i), o.observe(n), () => o.disconnect());
    }, []),
    {
      windowRef: e,
      bodyRef: t,
      overflow: n,
    }
  );
}
export function TypedCopy({ label: e, value: t, on: n }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <div
      className={
        'flex h-9 items-center gap-3 border-b border-[var(--line)] px-3 text-[13px]'
      }
    >
      <span className={'text-ink-4 w-[70px] shrink-0'}>
        {translateShowcaseNode(showcaseCopy, e)}
      </span>
      <span className={'text-ink min-w-0 truncate'}>
        {translateShowcaseNode(showcaseCopy, t)}
        {translateShowcaseNode(
          showcaseCopy,
          n ? <span className={'caret ml-px'} data-blink={'true'} /> : null
        )}
      </span>
    </div>
  );
}
export function MarketingEmail({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let { windowRef: t, bodyRef: n, overflow: r } = useTypedCopy(),
    i = Ql(wu, e, K.subject, 40),
    a = Ql(Tu, e, K.preview, 14),
    o = $l(e, K.scrollFrom, K.scrollTo) * r;
  return (
    <div className={'flex flex-col gap-4'}>
      <MarketPrompt
        t={e}
        workflow={'Create a Marketing Email'}
        brand={'aubrac'}
        prompt={`launch email for ${U.ref}`}
        doneAt={K.done}
        busy={'Writing the launch email'}
        result={'Launch email drafted'}
        meta={'Draft \xB7 not sent'}
      />
      <div className={'grid gap-3 lg:grid-cols-[minmax(0,1fr)_288px] lg:gap-4'}>
        <MarketReveal on={e >= K.frame} className={'mk-card overflow-hidden'}>
          <TypedCopy label={'From'} value={'Maison Aubrac'} on={!1} />
          <TypedCopy
            label={'Subject'}
            value={i}
            on={e >= K.subject && i.length < wu.length}
          />
          <TypedCopy
            label={'Preheader'}
            value={a}
            on={e >= K.preview && a.length < Tu.length}
          />
          <div
            ref={t}
            className={'relative h-[340px] overflow-hidden'}
            style={{
              background: '#F1EDE6',
            }}
          >
            <div
              ref={n}
              className={
                'mx-auto max-w-[440px] px-3 pt-4 pb-6 transition-transform duration-300 ease-[var(--ease)] sm:px-0'
              }
              style={{
                transform: `translateY(${-o}px)`,
              }}
            >
              <div
                className={'flex flex-col gap-4 px-5 pt-5 pb-6 sm:px-8'}
                style={{
                  background: Du,
                  color: Ou,
                }}
              >
                <MarketReveal
                  on={e >= K.mark}
                  className={'mk-serif mk-caps text-center text-[13px]'}
                >
                  {translateShowcaseNode(showcaseCopy, Eu('wordmark'))}
                </MarketReveal>
                <MarketReveal on={e >= K.hero}>
                  <MarketImage
                    asset={U.kit.launch}
                    focus={'50% 30%'}
                    alt={translateShowcaseNode(
                      showcaseCopy,
                      'The launch hero: the coat dress in a limestone room'
                    )}
                    className={'aspect-[5/6]'}
                  />
                </MarketReveal>
                <MarketReveal
                  on={e >= K.headline}
                  as={'h3'}
                  className={'mk-serif text-center text-[30px] leading-[1.05]'}
                >
                  {translateShowcaseNode(showcaseCopy, Eu('headline'))}
                </MarketReveal>
                <MarketReveal
                  on={e >= K.cta}
                  pop={!0}
                  className={
                    'mx-auto grid h-10 place-items-center rounded-[3px] px-5 text-[13px] font-medium'
                  }
                  style={{
                    background: Ou,
                    color: Du,
                  }}
                >
                  {translateShowcaseNode(showcaseCopy, Eu('cta'))}
                </MarketReveal>
                <MarketReveal
                  on={e >= K.section}
                  className={
                    'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-center gap-4 pt-4'
                  }
                  style={{
                    borderTop: `1px solid ${Au}`,
                  }}
                >
                  <span className={'mk-serif text-[22px] leading-[1.1]'}>
                    {translateShowcaseNode(showcaseCopy, Eu('section'))}
                  </span>
                  <MarketImage
                    asset={U.kit.detail}
                    focus={'50% 50%'}
                    alt={translateShowcaseNode(
                      showcaseCopy,
                      'The hand-finished hem and a covered button'
                    )}
                    className={'aspect-[4/5]'}
                  />
                </MarketReveal>
                <MarketReveal
                  on={e >= K.links}
                  className={'text-center text-[11px] leading-4'}
                  style={{
                    color: ku,
                  }}
                >
                  {translateShowcaseNode(showcaseCopy, Eu('links'))}
                </MarketReveal>
              </div>
            </div>
          </div>
        </MarketReveal>
        <div className={'flex flex-col gap-3'}>
          <MarketReveal on={e >= K.side} className={'mk-card p-3'}>
            <span className={'label'}>
              {translateShowcaseNode(showcaseCopy, 'Before sending')}
            </span>
            <p className={'text-ink-3 mt-1 text-[13px] leading-5 text-pretty'}>
              {translateShowcaseNode(showcaseCopy, ge.beforeSending)}
            </p>
          </MarketReveal>
          <MarketReveal
            on={e >= K.side + 200}
            className={
              'mk-card flex items-start gap-2.5 p-3 text-[13px] leading-5'
            }
          >
            <ShowcaseIcon
              name={'lock'}
              className={'text-ink-3 mt-0.5 size-4 shrink-0'}
            />
            <span>
              <span className={'text-ink block font-medium'}>
                {translateShowcaseNode(showcaseCopy, 'Draft, not sent')}
              </span>
              <span className={'text-ink-3'}>
                {translateShowcaseNode(
                  showcaseCopy,
                  'Sends only after your approval'
                )}
              </span>
            </span>
          </MarketReveal>
        </div>
      </div>
    </div>
  );
}
export const q = {
  frame: 300,
  gallery: 500,
  crumb: 750,
  title: 900,
  colour: 1500,
  size: 1700,
  bag: 2e3,
  block: 2300,
  blockStep: 650,
  land: 500,
};
export const Pu = q.block + Xl.length * q.blockStep + q.land;
export const Fu = '#FBF9F5';
export const Iu = '#2B2522';
export const Lu = '#857A6E';
export const Ru = '#E6DFD4';
export const zu = referenceImages.aubrac.listings.slice(0, 3);
export function EmailPreview({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = Ql(U.product, e, q.title, 45);
  return (
    <div className={'flex min-w-0 flex-col gap-3'}>
      <MarketReveal
        on={e >= q.gallery}
        className={'grid grid-cols-[36px_minmax(0,1fr)] gap-2'}
      >
        <span className={'flex flex-col gap-1.5'}>
          {translateShowcaseNode(
            showcaseCopy,
            zu.map((e, t) => (
              <span
                key={e.src}
                className={'relative block rounded-[4px]'}
                style={{
                  boxShadow:
                    t === 0 ? `0 0 0 1.5px ${Fu}, 0 0 0 2.5px ${Iu}` : void 0,
                }}
              >
                <MarketImage
                  asset={e}
                  className={'aspect-square rounded-[4px] bg-white'}
                />
              </span>
            ))
          )}
        </span>
        <MarketImage
          asset={U.image}
          alt={translateShowcaseNode(showcaseCopy, U.product)}
          className={'aspect-square rounded-[6px] bg-white'}
        />
      </MarketReveal>
      <div className={'flex flex-col text-[13px] leading-5'}>
        <MarketReveal
          on={e >= q.crumb}
          className={'mk-caps text-[10px]'}
          style={{
            color: Lu,
          }}
        >
          {translateShowcaseNode(showcaseCopy, 'Maison Aubrac \xB7 FW27')}
        </MarketReveal>
        <h3
          className={'mk-serif mt-0.5 min-h-[30px] text-[24px] leading-[30px]'}
        >
          {translateShowcaseNode(showcaseCopy, t)}
          {translateShowcaseNode(
            showcaseCopy,
            t.length > 0 && t.length < U.product.length ? (
              <span className={'caret'} data-blink={'true'} />
            ) : null
          )}
        </h3>
        <MarketReveal
          on={e >= q.colour}
          className={'mt-2 flex items-center gap-2'}
          style={{
            color: Lu,
          }}
        >
          <span
            className={'size-4 rounded-full'}
            style={{
              background: U.colours[0]?.hex,
              boxShadow: `0 0 0 1.5px ${Fu}, 0 0 0 2.5px ${Iu}`,
            }}
          />
          {translateShowcaseNode(
            showcaseCopy,
            'Umber black \xB7 double-faced wool'
          )}
        </MarketReveal>
        <div className={'mt-2.5 flex gap-1'}>
          {translateShowcaseNode(
            showcaseCopy,
            U.sizes.map((t, n) => (
              <MarketReveal
                key={t}
                pop={!0}
                on={e >= q.size + n * 50}
                as={'span'}
                className={
                  'num grid h-8 min-w-8 flex-1 place-items-center rounded-[4px] text-[12px]'
                }
                style={{
                  boxShadow: `inset 0 0 0 1px ${n === 2 ? Iu : Ru}`,
                }}
              >
                {translateShowcaseNode(showcaseCopy, t)}
              </MarketReveal>
            ))
          )}
        </div>
        <MarketReveal
          on={e >= q.bag}
          className={
            'mt-2.5 grid h-10 place-items-center rounded-[4px] text-[13px] font-medium'
          }
          style={{
            background: Iu,
            color: Fu,
          }}
        >
          {translateShowcaseNode(showcaseCopy, 'Add to bag')}
        </MarketReveal>
      </div>
    </div>
  );
}
